import React, { memo, ReactNode, useEffect, useState } from 'react'
import { NString } from 'types'
import cn from 'classnames'

import { ArrowRightIcon } from 'components'
import { formatDate, isValidDate } from 'utils'

import './styles.scss'
import {
  dayNamesRus,
  getDaysInMonth,
  getFirstDayOfMonth,
  getValidDate,
  monthNamesRus,
} from './utils'

const getPreviousMonthDays = (month: number, year: number, firstDay: number): ReactNode[] => {
  const days = []

  const prevMonth = month === 0 ? 11 : month - 1
  const prevYear = month === 0 ? year - 1 : year
  const prevMonthDays = getDaysInMonth(prevMonth, prevYear)

  for (let i = 0; i < firstDay; i++) {
    const day = prevMonthDays - firstDay + i + 1
    days.push(
      <div key={`prev-${day}`} className="day empty">
        {day}
      </div>,
    )
  }

  return days
}

interface ICurrentMonthDays {
  year: number
  month: number
  daysInMonth: number
  currentDate: Date
  onDateClick: (day: number, month: number, year: number) => void
  disabled?: boolean
  disablePast?: boolean
}

const getCurrentMonthDays = ({
  year,
  month,
  disabled,
  daysInMonth,
  currentDate,
  onDateClick,
  disablePast,
}: ICurrentMonthDays): ReactNode[] => {
  const currentDays = []
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  for (let i = 1; i <= daysInMonth; i++) {
    const date = new Date(year, month, i)
    const isSelected =
      isValidDate(currentDate.toISOString()) && date.toDateString() === currentDate.toDateString()

    const isToday = date.toDateString() === today.toDateString()
    const isPastDate = date < today

    const isDisabled = disabled || (disablePast && isPastDate)

    currentDays.push(
      <div
        key={`day-${i}`}
        className={cn('day', {
          disabled: isDisabled,
          selected: isSelected,
          today: isToday,
        } as any)}
        onClick={isDisabled ? undefined : () => onDateClick(i, month, year)}
      >
        {i}
      </div>,
    )
  }

  return currentDays
}

const getNextMonthDays = (daysInMonth: number, firstDay: number): ReactNode[] => {
  const days = []
  const nextMonthDays = 42 - (firstDay + daysInMonth) // 42 - общее кол-во ячеек в гриде календаря

  for (let i = 1; i <= nextMonthDays; i++) {
    days.push(
      <div key={`next-${i}`} className="day empty">
        {i}
      </div>,
    )
  }

  return days
}

interface IRenderCalendar {
  currentDate: Date
  onDateClick: (day: number, month: number, year: number) => void
  disabled?: boolean
  disablePast?: boolean
}

const renderCalendar = ({
  currentDate,
  onDateClick,
  disabled,
  disablePast,
}: IRenderCalendar): React.ReactNode[] => {
  const month = currentDate.getMonth()
  const year = currentDate.getFullYear()
  const daysInMonth = getDaysInMonth(month, year)
  const firstDay = getFirstDayOfMonth(month, year)

  const calendarDays = []

  calendarDays.push(...getPreviousMonthDays(month, year, firstDay))
  calendarDays.push(
    ...getCurrentMonthDays({
      year,
      month,
      disabled,
      daysInMonth,
      disablePast,
      currentDate,
      onDateClick,
    }),
  )
  calendarDays.push(...getNextMonthDays(daysInMonth, firstDay))

  return calendarDays
}

interface Props {
  onChange: (d: string) => void
  value?: NString
  disabled?: boolean
  disablePast?: boolean
  className?: string
  monthNames?: string[] // Можно передать переведенные на другой язык значения
  dayNames?: string[] // Можно передать переведенные на другой язык значения
}

export const Calendar = memo((props: Props) => {
  const {
    value,
    className,
    monthNames = monthNamesRus,
    dayNames = dayNamesRus,
    disabled,
    disablePast,
    onChange,
  } = props
  const initialDate = getValidDate(null)
  const [currentDate, setCurrentDate] = useState<Date>(initialDate)

  useEffect(() => {
    setCurrentDate(getValidDate(value))
  }, [value])

  const onPrevMonth = () => {
    let newMonth = currentDate.getMonth() - 1
    let newYear = currentDate.getFullYear()

    if (newMonth < 0) {
      newMonth = 11
      newYear -= 1
    }

    setCurrentDate(new Date(newYear, newMonth, 1))
  }

  const onNextMonth = () => {
    let newMonth = currentDate.getMonth() + 1
    let newYear = currentDate.getFullYear()

    if (newMonth > 11) {
      newMonth = 0
      newYear += 1
    }

    setCurrentDate(new Date(newYear, newMonth, 1))
  }

  const onDateClick = (day: number, month: number, year: number) => {
    if (disabled) {
      return
    }

    const newDate = new Date(year, month, day)
    const formatted = formatDate(newDate.toISOString(), 'yyyy-MM-dd')
    onChange(formatted)
  }

  return (
    <div className={cn('calendar', className)}>
      <div className="header">
        <button className="nav-button button-prev" onClick={onPrevMonth} disabled={disabled}>
          <ArrowRightIcon width={16} height={16} />
        </button>
        <div>
          {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
        </div>
        <button className="nav-button" onClick={onNextMonth} disabled={disabled}>
          <ArrowRightIcon width={16} height={16} />
        </button>
      </div>
      <div className="days">
        {dayNames.map((day) => (
          <div key={day} className="day-name">
            {day}
          </div>
        ))}
      </div>
      <div className="grid">
        {renderCalendar({ currentDate, onDateClick, disabled, disablePast })}
      </div>
    </div>
  )
})
